<template>
    <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
        :loading="false"
        :hasDownload="true" 
    >
      <template #subHeaderActions>
        <div class="d-flex align-items-center gap-2 gap-lg-3">
          <DateRangePicker id="stat-daterangepicker" @update:date="handleRangeDate" />
          <OutlineButton btn-class="btn-sm" icon-class="fas fa-download fs-3" type="primary" btn-text="button.download" @click="downloadView" :isLoading="isLoadingDownload"/>
        </div>
      </template>
      
      <template #layoutContent>

        <CrmOpportunitiesTotalByStatus :load-stats="loadWidgetStatData" :title="$t('pipelines.statistic')" id="statistic" />

        <div class="card shadow-sm mb-5 p-5" v-if="topSalespeople?.length > 0">
          <h3>{{ $t('pipelines.seller_performance') }}</h3>
          <div class="row mt-5 overflow-auto flex-nowrap min-w-100" style="white-space: nowrap;">
            <div class="col-12 col-md-6 col-lg-3 mb-4" v-for="(salePeople, index) in topSalespeople" :key="index">
              <div class="card p-3 h-100 bs-light-primary" >
                <div class="d-flex flex-stack mb-3">
                  <div class="d-flex cursor-pointer symbol">
                    <span class="symbol-label symbol-40px symbol-md-40px fw-bold text-primary img-size rounded-circle me-2" style="width: 20px; height: 20px; background-color: #d1e6f1;"> {{ initial(salePeople?.seller_name) }}</span><span class="mt-1">{{ salePeople?.seller_name }}</span>
                  </div>
                  <span class="text-end badge badge-primary">{{ $formatLocalAmount(salePeople?.total_revenue) }} {{defaultCurrency?.symbol}}</span>
                </div>
                <div class="d-flex flex-stack">
                  <div class="card px-3 pt-2 w-80px" v-for="(status, key) in salePeople?.opportunities">
                    <h6 class="font-weight-900">
                      <span class="me-2" :class="'text-'+filterStatus(key?.toUpperCase())[0]?.className">{{status}}</span>
                      {{filterStatus(key?.toUpperCase())[0]?.[locale]}}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CrmTopProspects :load-stats="loadWidgetStatData" :title="$t('pipelines.top_contacts')" id="top_contacts" />

        <CrmGetOpportunitiesConversionRate :load-stats="loadWidgetStatData" :title="$t('pipelines.exchange_customers')" id="kt_exchange_customers" />

        <CrmOpportunitiesByStage :load-stats="loadWidgetStatData" :title="$t('pipelines.total_by_step')" id="kt_total_by_step" />

        <CrmAverageDurationView :load-stats="loadWidgetStatData" :title="$t('pipelines.duration_opportunity_in_step')" id="kt_duration_opportunity_in_step" />
        
        <CrmAverageDurationByOutcome  :load-stats="loadWidgetStatData" :title="$t('pipelines.moyen_activity')" id="kt_moyen_activity"/>
        <CrmGetTopActivitiesForWonOpportunities  :load-stats="loadWidgetStatData" :title="$t('pipelines.top_activity')" id="kt_top_activity"/>
        <CrmTopOpportunitiesLostReason  :load-stats="loadWidgetStatData" :title="$t('pipelines.top_reason')" id="kt_top_reason"/>
      </template>
    </SubLayout>
</template>
<script setup>
  import {onMounted, ref, nextTick, shallowRef, computed, watch} from "vue";
  import {useStore} from "vuex";
  import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
  import i18n from '@/plugins/i18n';
  import {$formatLocalAmount} from "@/mixins/Helpers";
  import DateRangePicker from "@/components/DateRangePicker.vue";
  import { useLocales } from "@/composables/locales";
  import { useInitialName } from "@/composables/InitialName";
  import OutlineButton from "@/components/OutlineButton";
  import CrmAverageDurationView from "@/components/stats/widgets/CrmAverageDurationView.vue";
  import CrmAverageDurationByOutcome from "@/components/stats/widgets/CrmAverageDurationByOutcome.vue";
  import CrmGetTopActivitiesForWonOpportunities from "@/components/stats/widgets/CrmGetTopActivitiesForWonOpportunities.vue";
  import CrmTopOpportunitiesLostReason from "@/components/stats/widgets/CrmTopOpportunitiesLostReason.vue";
  import CrmOpportunitiesByStage from "@/components/stats/widgets/CrmOpportunitiesByStage.vue";
  import CrmGetOpportunitiesConversionRate from "@/components/stats/widgets/CrmGetOpportunitiesConversionRate.vue";
  import CrmTopProspects from "@/components/stats/widgets/CrmTopProspects.vue";
  import CrmOpportunitiesTotalByStatus from "@/components/stats/widgets/CrmOpportunitiesTotalByStatus.vue";
  
  const {locale} = useLocales()
  const store = useStore()

  const {initial} = useInitialName()
  
  const title = ref( "navigation.statistics")
  const breadcrumbs = ref([
    {
      name: 'navigation.pipelines',
      path: '/admin'
    }
  ])
  
  const query = ref({
    start_date: '',
    end_date: '',
    opportunity_id: '',
    seller_id: '',
    status: 'WON'
  })

  
  const topSalespeople = ref([])

  
  const isLoadingDownload = ref(false)
  
  const defaultCurrency = computed(() => store.getters['company/getCompanyCurrency'])

  watch(() => query.value.opportunity_id, (value) => {
    loadData()
  })

  watch(() => query.value.seller_id, (value) => {
    loadData()
  })

  watch(() => query.value.status, (value) => {
    loadData()
  })

  function handleRangeDate (range) {
    query.value.start_date = range.start_date
    query.value.end_date = range.end_date
    if(query.value.start_date && query.value.end_date) loadData()   
  }
  
  async function loadWidgetStatData(params) {
    try {
      const res = await store.dispatch('statistics/getPipelineStats', params)
      return res
    } finally { 
    }
  }

  function loadData(){
    if(query.value.start_date && query.value.end_date){
      Promise.all([
        store.dispatch('statistics/getPipelineStats', {startDate: query.value.start_date, endDate: query.value.end_date, opportunity_id: query.value.opportunity_id, seller_id: query.value.seller_id, status: query.value.status}).then((res) => {
          topSalespeople.value = res.data?.topSalespeople
        }),
      ]).then(() => {
        
      })
    }
   
  }
  
  onMounted(() => {
    let start = moment().subtract(29, 'days');
    let end = moment();
    query.value.start_date = start.format('DD-MM-YYYY')
    query.value.end_date = end.format('DD-MM-YYYY')
  
    if(query.value.start_date && query.value.end_date){
      nextTick(() => {
        loadData();
      });
    }
  })

  function downloadView(){
    isLoadingDownload.value = true
    store.dispatch('statistics/getDownloadPipelineStatistics', query.value)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', i18n.global.t('navigation.pipelines')+query.value.start_date+'.pdf');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.toastr['success'](i18n.global.t('notification.download_pipeline'))
        })
        .catch(err => {
        }).finally(() => isLoadingDownload.value = false)
  }
  
 

  

 

 

  
 
</script>
  
<style>
.symbol>img {
    width: 20px;
    height: 20px;
}
</style>